import { eq, useLiveQuery } from '@tanstack/react-db'
import { useState } from 'react'
import { activeLogs } from '@/db/application/callouts'
import { applyAddendums, orderAddendums } from '@/db/application/addendums'

import { getAllowedCalloutStatusTransitions } from '@/db/domain/calloutStatus'
import type { CalloutDetailsSavePayload, CalloutDetailsScreenProps } from '@/screens/CalloutDetails.types'
import { useDb } from '@/lib/dbContext'
import type { Addendum, AddendumChange, Callout, Customer, Note, ServiceRevision, StatusChangeLog } from '@/lib/schemas/db'
import { withSpan } from '@/lib/sentry'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'
import { useEnsureServiceLoaded } from '@/screens/useEnsureServiceLoaded'

type DetailsState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_found' }
  | { kind: 'ready'; screenProps: CalloutDetailsScreenProps }

function logsToNotes(
  logs: Note[] | undefined,
  getAuthorDisplayName: (authorId: string) => string,
) {
  // Soft-deleted notes are no longer part of the record; the audit trail for
  // them lives on the Log Work feed.
  const visible = activeLogs(logs ?? [])
  if (visible.length === 0) return []

  return visible.map((log) => {
    if (log.photoUrl) {
      return {
        id: log.id,
        kind: 'photo' as const,
        authorName: getAuthorDisplayName(log.authorId),
        createdAt: log.createdAt,
        photoUrl: log.photoUrl,
        photoUrls: log.photoUrls,
        caption: log.caption ?? '',
      }
    }

    return {
      id: log.id,
      kind: 'text' as const,
      authorName: getAuthorDisplayName(log.authorId),
      createdAt: log.createdAt,
      text: log.text ?? '',
    }
  })
}

export function useCalloutDetails(serviceIdentity: string): DetailsState {
  const db = useDb()
  const [amending, setAmending] = useState(false)
  const tryPublicId = looksLikePublicId(serviceIdentity)

  const calloutByIdResult = useLiveQuery(
    (q) =>
      q
        .from({ callouts: db.calloutsCollection })
        .where(({ callouts }) => eq(callouts.id, serviceIdentity))
        .findOne(),
    [serviceIdentity],
  )

  const calloutByPublicIdResult = useLiveQuery(
    (q) =>
      tryPublicId
        ? q
            .from({ callouts: db.calloutsCollection })
            .where(({ callouts }) => eq(callouts.publicId, serviceIdentity))
            .findOne()
        : null,
    [serviceIdentity, tryPublicId],
  )

  const callout =
    (calloutByIdResult.data as Callout | undefined) ??
    (calloutByPublicIdResult.data as Callout | undefined)
  const serviceId = callout?.id ?? serviceIdentity
  if (!tryPublicId && callout && (calloutByIdResult.data as Callout | undefined)) {
    captureLegacyIdRoute('callout', serviceIdentity)
  }

  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))

  const notesResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ notes: db.notesCollection })
            .where(({ notes }) => eq(notes.serviceId, serviceId))
            .orderBy(({ notes }) => notes.createdAt)
        : null,
    [serviceId],
  )

  const addendumsResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ addendums: db.addendumsCollection })
            .where(({ addendums }) => eq(addendums.serviceId, serviceId))
            .orderBy(({ addendums }) => addendums.createdAt)
        : null,
    [serviceId],
  )

  const revisionsResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ revisions: db.calloutRevisionsCollection })
            .where(({ revisions }) => eq(revisions.serviceId, serviceId))
            .orderBy(({ revisions }) => revisions.createdAt)
        : null,
    [serviceId],
  )

  const statusChangesResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ statusChanges: db.statusChangesCollection })
            .where(({ statusChanges }) => eq(statusChanges.serviceId, serviceId))
            .orderBy(({ statusChanges }) => statusChanges.createdAt)
        : null,
    [serviceId],
  )

  const customerResult = useLiveQuery(
    (q) => {
      const customerId = callout?.customerId
      if (!customerId) return null

      return q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, customerId))
        .findOne()
    },
    [callout?.customerId],
  )

  // Archived callouts aren't in the live sync; pull one in on demand.
  const recordQueriesSettled =
    !calloutByIdResult.isLoading &&
    !calloutByPublicIdResult.isLoading &&
    !calloutByIdResult.isError &&
    !calloutByPublicIdResult.isError
  const { hydrating } = useEnsureServiceLoaded(
    'callout',
    serviceIdentity,
    recordQueriesSettled && !callout,
  )

  if (
    calloutByIdResult.isLoading ||
    calloutByPublicIdResult.isLoading ||
    usersResult.isLoading ||
    notesResult.isLoading ||
    statusChangesResult.isLoading ||
    hydrating
  ) {
    return { kind: 'loading' }
  }

  if (
    calloutByIdResult.isError ||
    calloutByPublicIdResult.isError ||
    usersResult.isError ||
    notesResult.isError ||
    statusChangesResult.isError
  ) {
    return { kind: 'error' }
  }

  if (!callout) {
    return { kind: 'not_found' }
  }

  const customer = customerResult.data as Customer | undefined
  const customerName = customer
    ? customer.externalId
      ? `${customer.fullName} – ${customer.externalId}`
      : customer.fullName
    : callout.contactName ?? 'Customer'

  const noteList = logsToNotes(
    notesResult.data as Note[] | undefined,
    db.getAuthorDisplayName,
  )
  const statusTransitions = getAllowedCalloutStatusTransitions(callout.status)

  const handleSaveDetails = ({
    contactAddress,
    contactEmail,
    contactName,
    contactPhone,
    invoiceAddress,
    invoiceEmail,
    invoiceId,
    invoiceName,
    invoicePhone,
    invoiceVat,
    issueSummary,
    startTime,
    endTime,
    isWholeDay,
  }: CalloutDetailsSavePayload) => {
    db.updateCallout(callout.id, {
      contactAddress,
      contactName,
      contactPhone,
      contactEmail,
      invoiceName,
      invoiceAddress,
      invoiceEmail,
      invoicePhone,
      invoiceVat,
      invoiceId,
      issueSummary,
      startTime,
      endTime,
      isWholeDay,
    })
  }

  const handleChangeCustomer = (customerId: string) => {
    db.updateCallout(callout.id, {
      customerId,
    })
  }

  const handleChangeStatus = async (status: Callout['status'], reason?: string) =>
    withSpan(
      {
        op: 'callout.status.change',
        name: 'Change callout status (details)',
        attributes: { serviceId: callout.id, nextStatus: status },
      },
      () =>
        db.updateCalloutAndWait(callout.id, {
          status,
          statusReason:
            status === 'Review' || status === 'Cancelled'
              ? reason?.trim() || `Changed from callout details to ${status}`
              : undefined,
        }),
    )

  const handleAddNote = (text: string) => {
    if (!text.trim()) return false
    db.appendLog(callout.id, { text })
    return true
  }

  const handleUpdateNote = (
    id: string,
    patch: { text?: string; caption?: string },
  ) => {
    db.updateLog(callout.id, id, patch)
  }

  const handleDeleteNote = (id: string) => {
    db.deleteLog(callout.id, id)
  }

  const addendums = orderAddendums(
    (addendumsResult.data as Addendum[] | undefined) ?? [],
  )

  // The stored record still holds the values as locked; addendums layer on top
  // to give what is in force now.
  const effective = applyAddendums(
    callout as unknown as Record<string, unknown>,
    addendums,
  ) as unknown as typeof callout

  const handleSaveAddendum = async (
    reason: string,
    changes: AddendumChange[],
  ) => {
    await db.appendAddendumAndWait(callout.id, { reason, changes })
    setAmending(false)
  }

  return {
    kind: 'ready',
    screenProps: {
      serviceId: callout.id,
      publicId: callout.publicId,
      customerId: callout.customerId,
      customerPublicId: customer?.publicId,
      customerName,
      linkedCustomer: customer
        ? {
            fullName: customer.fullName,
            externalId: customer.externalId,
            contactEmail: customer.contactEmail,
            contactPhone: customer.contactPhone,
            contactAddress: customer.contactAddress,
            invoiceName: customer.invoiceName,
            invoiceEmail: customer.invoiceEmail,
            invoicePhone: customer.invoicePhone,
            invoiceAddress: customer.invoiceAddress,
            invoiceVat: customer.invoiceVat,
          }
        : undefined,
      status: callout.status,
      statusTransitions,
      contactAddress: effective.contactAddress,
      contactName: effective.contactName,
      contactPhone: effective.contactPhone,
      contactEmail: effective.contactEmail,
      invoiceName: effective.invoiceName,
      invoiceAddress: effective.invoiceAddress,
      invoiceEmail: effective.invoiceEmail,
      invoicePhone: effective.invoicePhone,
      invoiceVat: effective.invoiceVat,
      invoiceId: effective.invoiceId,
      issueSummary: effective.issueSummary,
      startTime: callout.startTime,
      endTime: callout.endTime,
      isWholeDay: callout.isWholeDay,
      notes: noteList,
      statusChanges: statusChangesResult.data as StatusChangeLog[] | undefined,
      getAuthorDisplayName: db.getAuthorDisplayName,
      onSaveDetails: handleSaveDetails,
      onChangeStatus: handleChangeStatus,
      onChangeCustomer: handleChangeCustomer,
      onAddNote: handleAddNote,
      onUpdateNote: handleUpdateNote,
      onDeleteNote: handleDeleteNote,
      addendums,
      revisions: (revisionsResult.data as ServiceRevision[] | undefined) ?? [],
      lockedValues: {
        issueSummary: callout.issueSummary,
        contactAddress: callout.contactAddress,
        contactName: callout.contactName,
        contactPhone: callout.contactPhone,
        contactEmail: callout.contactEmail,
        invoiceName: callout.invoiceName,
        invoiceEmail: callout.invoiceEmail,
        invoicePhone: callout.invoicePhone,
        invoiceAddress: callout.invoiceAddress,
        invoiceVat: callout.invoiceVat,
        invoiceId: callout.invoiceId,
      },
      amending,
      onStartAmend: () => setAmending(true),
      onDiscardAmend: () => setAmending(false),
      onSaveAddendum: handleSaveAddendum,
    },
  }
}
